import { useState, useRef, useEffect } from "react";
import { TransformWrapper, TransformComponent, getCenterPosition } from "react-zoom-pan-pinch";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { MdZoomIn, MdZoomOut, MdCenterFocusWeak } from "react-icons/md";
import { css } from "@emotion/css";

const pinchzoomContainer = css`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const pinchzoomTip = css`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(18, 18, 18, 0.75);
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 10;
  backdrop-filter: blur(4px);
  font-size: 14px;
  max-width: 90%;
  text-align: center;

  @media (max-width: 768px) {
    top: 12px;
    padding: 6px 10px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    top: 8px;
    padding: 4px 8px;
    font-size: 11px;
  }
`;

const pinchzoomControls = css`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  background-color: rgba(18, 18, 18, 0.75);
  padding: 6px 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 10;
  backdrop-filter: blur(4px);

  @media (max-width: 768px) {
    bottom: 12px;
    right: 12px;
    padding: 4px 8px;
  }

  @media (max-width: 480px) {
    bottom: 8px;
    right: 8px;
  }
`;

const pinchzoomControlButton = css`
  background: none;
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const pinchzoomScaleDisplay = css`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0 8px;
  font-family: "JetBrains Mono", monospace;
  min-width: 40px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 12px;
    margin: 0 6px;
    min-width: 36px;
  }
`;

interface PinchZoomProps {
  className?: string;
  children: React.ReactNode;
}

const PinchZoom = ({ className, children }: PinchZoomProps) => {
  const [showTip, setShowTip] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const wrapperRef = useRef<ReactZoomPanPinchRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [displayScale, setDisplayScale] = useState(1);
  const MIN_SCALE = 1;
  const MAX_SCALE = 7;

  useEffect(() => {
    const checkIfMobile = () => {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const userAgentMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      setIsMobile(isTouchDevice || userAgentMobile);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Show tip once
  useEffect(() => {
    setShowTip(true);
  }, []);

  const checkBounds = (positionX: number, positionY: number) => {
    if (!containerRef.current) return { x: positionX, y: positionY };
    const containerBounds = containerRef.current.getBoundingClientRect();
    const contentElement = containerRef.current.querySelector(
      '[class*="react-transform-component"]'
    ) as HTMLElement | null;
    if (!contentElement) return { x: positionX, y: positionY };
    const contentBounds = contentElement.getBoundingClientRect();

    const extraHorizontalAllowance = containerBounds.width / 2;
    const extraVerticalAllowance = containerBounds.height / 2;

    let newX = positionX;
    let newY = positionY;

    if (contentBounds.left > containerBounds.left + extraHorizontalAllowance) {
      newX = positionX - (contentBounds.left - containerBounds.left - extraHorizontalAllowance);
    } else if (contentBounds.right < containerBounds.right - extraHorizontalAllowance) {
      newX = positionX + (containerBounds.right - contentBounds.right - extraHorizontalAllowance);
    }

    if (contentBounds.top > containerBounds.top + extraVerticalAllowance) {
      newY = positionY - (contentBounds.top - containerBounds.top - extraVerticalAllowance);
    } else if (contentBounds.bottom < containerBounds.bottom - extraVerticalAllowance) {
      newY = positionY + (containerBounds.bottom - contentBounds.bottom - extraVerticalAllowance);
    }

    return { x: newX, y: newY };
  };

  const handleZoomOut = () => {
    const apiRef = wrapperRef.current;
    const container = containerRef.current;
    if (!apiRef || !container) return;
    const inst = apiRef.instance;
    if (!inst) return;
    const current = inst.transformState;
    const step = 0.2;
    const targetScale = Math.max(MIN_SCALE, current.scale - step);
    if (targetScale === current.scale) return;
    const k = targetScale / current.scale;
    const rect = container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const targetX = current.positionX * k + cx * (1 - k);
    const targetY = current.positionY * k + cy * (1 - k);
    const bounded = checkBounds(targetX, targetY);
    apiRef.setTransform(bounded.x, bounded.y, targetScale, 300, "easeOut");
  };

  const handleZoomIn = () => {
    const apiRef = wrapperRef.current;
    const container = containerRef.current;
    if (!apiRef || !container) return;
    const inst = apiRef.instance;
    if (!inst) return;
    const current = inst.transformState;
    const step = 0.2;
    const targetScale = Math.min(MAX_SCALE, current.scale + step);
    if (targetScale === current.scale) return;
    const k = targetScale / current.scale;
    const rect = container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const targetX = current.positionX * k + cx * (1 - k);
    const targetY = current.positionY * k + cy * (1 - k);
    const bounded = checkBounds(targetX, targetY);
    apiRef.setTransform(bounded.x, bounded.y, targetScale, 300, "easeOut");
  };

  const handleReset = () => {
    const apiRef = wrapperRef.current;
    if (!apiRef) return;
    const wrapper = apiRef.instance?.wrapperComponent ?? null;
    const content = apiRef.instance?.contentComponent ?? null;
    if (wrapper && content) {
      const center = getCenterPosition(1, wrapper, content);
      apiRef.setTransform(center.positionX, center.positionY, 1, 300, "easeOut");
    } else {
      apiRef.resetTransform(300, "easeOut");
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${className || ""} ${pinchzoomContainer}`}
    >
      <TransformWrapper
        ref={wrapperRef}
        initialScale={1}
        minScale={1}
        maxScale={7}
        centerOnInit
        smooth={false}
        zoomAnimation={{ disabled: true }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: false }}
        limitToBounds={false}
        centerZoomedOut={false}
        onTransformed={({ state }) => {
          setDisplayScale(state.scale);
        }}
        onPanningStop={({ state }) => {
          const bounded = checkBounds(state.positionX, state.positionY);
          if (bounded.x !== state.positionX || bounded.y !== state.positionY) {
            wrapperRef.current?.setTransform(bounded.x, bounded.y, state.scale, 300, "easeOut");
          }
        }}
        onZoomStop={({ state }) => {
          const bounded = checkBounds(state.positionX, state.positionY);
          if (bounded.x !== state.positionX || bounded.y !== state.positionY) {
            wrapperRef.current?.setTransform(bounded.x, bounded.y, state.scale, 300, "easeOut");
          }
        }}
      >
        {() => (
          <>
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "95%",
                  height: "95%",
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {children}
              </div>
            </TransformComponent>

            {showTip && (
              <div className={pinchzoomTip}>
                {isMobile ? (
                  <span>Use os dedos para ampliar ou diminuir o mapa</span>
                ) : (
                  <span>Segure Ctrl + Roda do mouse para controlar o zoom</span>
                )}
              </div>
            )}

            <div className={pinchzoomControls}>
              <button
                className={pinchzoomControlButton}
                onClick={handleZoomOut}
                aria-label="Zoom out"
              >
                <MdZoomOut size={18} />
              </button>
              <div className={pinchzoomScaleDisplay}>
                {Math.round(displayScale * 100)}%
              </div>
              <button
                className={pinchzoomControlButton}
                onClick={handleZoomIn}
                aria-label="Zoom in"
              >
                <MdZoomIn size={18} />
              </button>
              <button
                className={pinchzoomControlButton}
                onClick={handleReset}
                aria-label="Reset zoom"
              >
                <MdCenterFocusWeak size={18} />
              </button>
            </div>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default PinchZoom;
